import type { EditorElement } from '@wix/component-protocol/schema';
import { SYSTEM_CONTAINER_TYPE } from '../constants/componentTypes';

import { ManifestComponentExtensionType } from '../stores';
import { AbstractManifestMiddleware } from './AbstractManifestMiddleware';
import { ContainerPlatformService } from '../componentsPlatformServices';
import {
  VirtualElement,
  VirtualElementAPIType,
} from '../api/providers/VirtualElement';
import { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

const POSSIBLE_PLUGIN_EXTENSION_TYPES = [
  ManifestComponentExtensionType.BUILDER,
  ManifestComponentExtensionType.OOI,
  ManifestComponentExtensionType.CUSTOM_ELEMENT,
];

export class PluginOverrideMiddleware extends AbstractManifestMiddleware {
  constructor(
    editorPointerAPI: EditorPointerServiceAPI,
    private readonly containerPlatformService: ContainerPlatformService,
  ) {
    super(editorPointerAPI);
  }

  #isPlugin(node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>) {
    const extensionType = node.getExtensionType();

    if (!POSSIBLE_PLUGIN_EXTENSION_TYPES.includes(extensionType)) {
      return false;
    }

    const componentType = node.getComponentType();

    if (componentType === SYSTEM_CONTAINER_TYPE) {
      return false;
    }

    const editorPointer = node.getEditorPointer();

    const containerOwnerPointer =
      this.containerPlatformService.getContainerOwnerPointer(editorPointer);

    if (!containerOwnerPointer) {
      return false;
    }

    const slots = this.containerPlatformService.getPluginSlots(
      containerOwnerPointer,
    );

    return slots.find(
      ({ compRef }) => compRef?.id === editorPointer.documentPointer.id,
    );
  }

  #override(element: EditorElement) {
    return {
      ...element,
      layout: {
        ...element.layout,
        disablePositioning: true,
        disableStretching: true,
        disableRotation: true,
        disableDuplication: true,
      },
    };
  }

  resolveRootElement: AbstractManifestMiddleware['resolveRootElement'] = (
    node,
  ) => {
    const manifest = node.getManifest();

    if (!manifest) {
      return null;
    }

    if (!this.#isPlugin(node)) {
      return null;
    }

    const overrides = this.#override(manifest.editorElement!);

    return overrides ? { editorElement: overrides } : null;
  };

  resolveChildRefElement: AbstractManifestMiddleware['resolveChildRefElement'] =
    () => {
      return null;
    };
}
